// @flow

/**
 * Column definitions for different table types
 * Each column specifies width, flex behavior, and styling
 */

type ColumnConfig = {
  key: string,
  width?: number,
  minWidth?: number,
  maxWidth?: number,
  flex?: string,
  hideOnMobile?: boolean,
  sortable?: boolean,
  headerLabel: string
};

type ActionConfig = {
  key: string,
  icon: any,
  label: string,
  onClick: string, // method name to call
  showCondition?: (item: any) => boolean
};

type TableConfig = {
  columns: Array<ColumnConfig>,
  actions: Array<ActionConfig>,
  defaultSort?: string,
  enableMultiSelect?: boolean
};

// Column width constants
export const COLUMN_WIDTHS = {
  CHECKBOX: 44,
  SMALL: 100,
  MEDIUM: 150,
  LARGE: 200,
  XLARGE: 250,
  FLEX: '1 1 auto',
  FIXED_SMALL: '0 0 auto',
  ACTIONS: 220
};

// Tags table configuration
export const TAGS_TABLE_CONFIG: TableConfig = {
  columns: [
    {
      key: 'name',
      headerLabel: 'Tag Name',
      flex: COLUMN_WIDTHS.FLEX,
      minWidth: COLUMN_WIDTHS.LARGE,
      sortable: true
    },
    {
      key: 'customerCount',
      headerLabel: 'Customers',
      width: COLUMN_WIDTHS.LARGE,
      flex: COLUMN_WIDTHS.FIXED_SMALL,
      sortable: true
    }
  ],
  actions: [
    {
      key: 'edit',
      icon: 'EditIcon',
      label: 'Edit Tag',
      onClick: 'handleEditTag'
    },
    {
      key: 'remove',
      icon: 'RemoveIcon',
      label: 'Remove Tag',
      onClick: 'handleRemoveTag'
    }
  ],
  defaultSort: 'name',
  enableMultiSelect: false
};

// Tenants table configuration
export const TENANTS_TABLE_CONFIG: TableConfig = {
  columns: [
    {
      key: 'checkbox',
      headerLabel: '',
      width: COLUMN_WIDTHS.CHECKBOX,
      flex: COLUMN_WIDTHS.FIXED_SMALL,
      hideOnMobile: true
    },
    {
      key: 'name',
      headerLabel: 'Tenant',
      width: COLUMN_WIDTHS.XLARGE,
      flex: COLUMN_WIDTHS.FIXED_SMALL,
      sortable: true
    },
    {
      key: 'tags',
      headerLabel: 'Tag',
      width: 300,
      flex: COLUMN_WIDTHS.FIXED_SMALL
    },
    {
      key: 'phone',
      headerLabel: 'Phone',
      width: 175,
      flex: COLUMN_WIDTHS.FIXED_SMALL
    },
    {
      key: 'email',
      headerLabel: 'Email',
      width: COLUMN_WIDTHS.MEDIUM,
      flex: COLUMN_WIDTHS.FIXED_SMALL
    },
    {
      key: 'dateAdded',
      headerLabel: 'Date Added',
      flex: COLUMN_WIDTHS.FLEX,
      minWidth: COLUMN_WIDTHS.MEDIUM,
      sortable: true
    }
  ],
  actions: [
    {
      key: 'message',
      icon: 'icon-sent', // String for icon class
      label: 'Send a message',
      onClick: 'handleSendMessage'
    },
    {
      key: 'edit',
      icon: 'EditIcon',
      label: 'Edit Tenant',
      onClick: 'handleEditTenant'
    },
    {
      key: 'remove',
      icon: 'RemoveIcon',
      label: 'Remove Tenant',
      onClick: 'handleRemoveTenant'
    }
  ],
  defaultSort: 'name',
  enableMultiSelect: true
};

/**
 * Generate cell styles for a column configuration
 */
export const generateCellStyles = (column: ColumnConfig, theme: any, isHeader: boolean = false, columnIndex: number = 0): Object => {
  const baseStyles: Object = {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    padding: isHeader ? `${theme.spacing(2)} ${theme.spacing(1)}` : `${theme.spacing(1)} ${theme.spacing(1)}`,
    background: 'white',
    border: 'none'
  };

  // Only apply minHeight to body cells, not header cells
  if (!isHeader) {
    baseStyles.minHeight = 42;
  }

  // Apply left padding to first cell if it's not a checkbox
  if (columnIndex === 0 && column.key !== 'checkbox') {
    const verticalPadding = isHeader ? theme.spacing(2) : theme.spacing(1);
    baseStyles.padding = `${verticalPadding} ${theme.spacing(1)} ${verticalPadding} ${theme.spacing(4)}`;
  }

  // Handle flex vs fixed width
  if (column.flex) {
    baseStyles.flex = column.flex;
  } else {
    baseStyles.flex = '0 0 auto';
  }

  // Set width constraints
  if (column.width) {
    baseStyles.width = column.width;
    baseStyles.maxWidth = column.width;
  }
  if (column.minWidth) {
    baseStyles.minWidth = column.minWidth;
  }
  if (column.maxWidth) {
    baseStyles.maxWidth = column.maxWidth;
  }

  // Add pointer cursor for sortable columns
  if (column.sortable) {
    baseStyles.cursor = 'pointer';
  }
  return baseStyles;
};

/**
 * Generate action cell styles with hover behavior
 */
export const generateActionCellStyles = (actionsCount: number, theme: any): Object => ({
  display: 'none', // Hidden by default
  flex: '0 0 auto',
  minWidth: theme.layout.listTable.rowHeight * actionsCount,
  maxWidth: theme.layout.listTable.rowHeight * actionsCount,
  paddingRight: 0,
  paddingLeft: 0,
  backgroundColor: 'transparent',
  justifyContent: 'flex-end',
  gap: 0, // No gap for seamless button layout
  
  // When row is hovered, show the action cell matching row background
  '.hover &': {
    display: 'flex',
    backgroundColor: 'hsl(0,0%,97%)', // Match row background exactly
    alignItems: 'stretch', // Stretch buttons to full height
    '& button': {
      backgroundColor: theme.palette.primary.lighter, // Use same color as FeedRow actions
      color: 'white',
      borderRadius: 0, // No border radius to fill height completely
      height: '100%', // Full row height
      minHeight: theme.layout.listTable.rowHeight,
      width: theme.layout.listTable.rowHeight,
      minWidth: theme.layout.listTable.rowHeight,
      margin: 0,
      '&:hover': {
        backgroundColor: theme.palette.primary.main // Darker on hover
      },
      '&:last-child': {
        borderTopRightRadius: theme.shape.borderRadius,
        borderBottomRightRadius: theme.shape.borderRadius
      }
    }
  }
}); 